import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, ArrowRight, Share2, Sparkles, Phone, CheckCircle, Clock } from 'lucide-react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useCmsContent } from '../context/CmsImagesContext';

function formatDate(dateStr, lang = 'da') {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return new Intl.DateTimeFormat(lang === 'da' ? 'da-DK' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(d);
  } catch {
    return dateStr;
  }
}

function isCtaEntry(item) {
  if (!item) return false;
  const slugStr = typeof item.slug === 'object' ? (item.slug?.da || Object.values(item.slug)[0] || '') : (item.slug || '');
  const titleStr = typeof item.titel === 'object' ? (item.titel?.da || Object.values(item.titel)[0] || '') : (item.titel || '');
  return (
    slugStr.toLowerCase() === 'cta' ||
    slugStr.toLowerCase() === 'bundsektion-cta' ||
    slugStr.toLowerCase() === 'nyhedsside-cta' ||
    titleStr.toLowerCase().includes('cta') ||
    titleStr.toLowerCase().includes('bundsektion')
  );
}

export default function NewsDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { companyInfo } = useCmsContent();
  const [article, setArticle] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const getLocalizedField = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] || field.da || Object.values(field)[0] || '';
    }
    return '';
  };

  const getSlug = (item) => {
    if (!item) return '';
    if (item.slug) {
      if (typeof item.slug === 'string') return item.slug;
      if (typeof item.slug === 'object') {
        const langSlug = item.slug[language];
        if (langSlug && !/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(langSlug)) return langSlug;
        if (item.slug.da) return item.slug.da;
        return langSlug || Object.values(item.slug)[0] || item.id;
      }
    }
    return item.id;
  };

  const getImageSrc = (item) => {
    if (!item) return '/images/hero-cleaning.png';
    let url = null;
    if (Array.isArray(item.billede) && item.billede.length > 0) {
      url = item.billede[0];
    } else if (typeof item.billede === 'string' && item.billede.trim()) {
      url = item.billede.trim();
    }
    if (url && typeof url === 'string') {
      if (url.startsWith('https://assets.auxodev.dk/') && !url.includes('/cdn-cgi/image/')) {
        return url.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/');
      }
      return url;
    }
    return '/images/hero-cleaning.png';
  };

  useEffect(() => {
    let isMounted = true;

    async function loadArticle() {
      try {
        setLoading(true);
        let foundArticle = null;

        // 1. Try public slug endpoint
        try {
          const langCode = language === 'en' ? 'en' : 'da';
          const res = await fetch(`https://api.dev.auxo.dk/public/by-slug/${langCode}/${encodeURIComponent(slug)}`);
          if (res.ok) {
            const data = await res.json();
            if (data && (data.titel || data.id)) {
              foundArticle = data;
            }
          } else if (langCode !== 'da') {
            const fallbackDaRes = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${encodeURIComponent(slug)}`);
            if (fallbackDaRes.ok) {
              const data = await fallbackDaRes.json();
              if (data && (data.titel || data.id)) {
                foundArticle = data;
              }
            }
          }
        } catch (slugErr) {
          console.warn('Slug endpoint check failed:', slugErr);
        }

        // 2. Fallback: fetch from v2/content/nyheder and find by slug or id
        try {
          const listRes = await fetch('https://api.dev.auxo.dk/public/v2/content/nyheder?sort=dato&order=desc');
          if (listRes.ok) {
            const listData = await listRes.json();
            const items = Array.isArray(listData.items) ? listData.items : (Array.isArray(listData) ? listData : []);
            
            if (!foundArticle) {
              foundArticle = items.find((item) => {
                const daSlug = typeof item.slug === 'object' ? item.slug.da : item.slug;
                const enSlug = typeof item.slug === 'object' ? item.slug.en : item.slug;
                const activeSlug = getSlug(item);
                return (
                  item.id === slug ||
                  activeSlug === slug ||
                  activeSlug === decodeURIComponent(slug) ||
                  daSlug === slug ||
                  daSlug === decodeURIComponent(slug) ||
                  enSlug === slug ||
                  enSlug === decodeURIComponent(slug)
                ) && !isCtaEntry(item);
              });
            } else if (isCtaEntry(foundArticle)) {
              foundArticle = null;
            }

            if (isMounted) {
              // Set recent articles excluding the current one and excluding the CTA post
              const others = items.filter((item) => {
                const itemSlug = getSlug(item);
                return !isCtaEntry(item) && itemSlug !== slug && item.id !== slug;
              }).slice(0, 3);
              setRecentArticles(others);
            }
          }
        } catch (listErr) {
          console.error('Error fetching news list for fallback:', listErr);
        }

        if (isMounted) {
          setArticle(foundArticle);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadArticle();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (article) {
      const title = getLocalizedField(article.titel);
      document.title = title ? `${title} | FTR Service ApS` : "Nyhed | FTR Service ApS";
    }
  }, [article, language]);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="py-32 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-6 bg-gray-200 rounded w-28" />
            <div className="h-10 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/3" />
            <div className="h-80 bg-gray-200 rounded-3xl" />
            <div className="space-y-3 pt-6">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
              <div className="h-4 bg-gray-200 rounded w-4/6" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="py-32 max-w-xl mx-auto px-4 text-center">
          <div className="bg-white p-10 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">
              {t({ da: "Nyheden blev ikke fundet", en: "Article not found" })}
            </h2>
            <p className="text-gray-500 text-sm">
              {t({
                da: "Den artikel du leder efter eksisterer muligvis ikke længere eller er blevet flyttet.",
                en: "The article you are looking for may no longer exist or has been moved."
              })}
            </p>
            <div className="pt-4">
              <Link
                to="/nyheder"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-primary text-white font-semibold text-sm hover:bg-[#152744] transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t({ da: "Tilbage til alle nyheder", en: "Back to all news" })}</span>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const title = getLocalizedField(article.titel);
  const excerpt = getLocalizedField(article.uddrag);
  const rawBody = getLocalizedField(article.beskrivelse) || getLocalizedField(article.indhold) || excerpt || '';
  const isHtml = /<[a-z][\s\S]*>/i.test(rawBody);
  const imageSrc = getImageSrc(article);
  const formattedDate = formatDate(article.dato, language);
  const author = article.forfatter || 'FTR Service';

  const calculateReadTime = (text) => {
    if (!text) return '1 min.';
    const clean = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const wordCount = clean.split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.round(wordCount / 180));
    return `${minutes} min.`;
  };
  const readTime = calculateReadTime(rawBody);

  return (
    <Layout>
      {/* Header & Breadcrumb */}
      <section className="bg-gray-50 pt-28 pb-12 border-b border-gray-200/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* Navigation back */}
            <div className="flex items-center justify-between">
              <Link
                to="/nyheder"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-brand-primary transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t({ da: "Tilbage til nyhedsoversigt", en: "Back to news overview" })}</span>
              </Link>

              <button
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-600 hover:text-brand-primary hover:border-brand-primary transition-all shadow-sm"
                title={t({ da: "Kopier link", en: "Copy link" })}
              >
                <Share2 className="w-3.5 h-3.5" />
                <span>{copied ? t({ da: "Link kopieret!", en: "Link copied!" }) : t({ da: "Del", en: "Share" })}</span>
              </button>
            </div>

            {/* Meta badges */}
            <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-500 font-medium">
              {formattedDate && (
                <div className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-gray-200/80 shadow-xs">
                  <Calendar className="w-3.5 h-3.5 text-[#1877F2]" />
                  <span>{formattedDate}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-gray-200/80 shadow-xs">
                <User className="w-3.5 h-3.5 text-[#1877F2]" />
                <span>{author}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full border border-gray-200/80 shadow-xs">
                <Clock className="w-3.5 h-3.5 text-[#1877F2]" />
                <span>{readTime} {t({ da: "læsetid", en: "read" })}</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight">
              {title}
            </h1>

            {/* Excerpt Lead */}
            {excerpt && excerpt !== rawBody && (
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed font-normal border-l-4 border-[#85d7f9] pl-4">
                {excerpt}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Main Content Article */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Image */}
          {imageSrc && (
            <div className="mb-12 rounded-3xl overflow-hidden shadow-lg border border-gray-100 max-h-[480px]">
              <img
                src={imageSrc}
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Rich text body */}
          <article className="prose prose-lg max-w-none text-gray-700 prose-headings:font-bold prose-headings:text-gray-900 prose-h2:text-2xl prose-h3:text-xl prose-p:leading-relaxed prose-p:my-4 prose-ul:my-4 prose-ul:space-y-2 prose-li:text-gray-700 prose-a:text-[#1877F2] prose-a:underline hover:prose-a:text-brand-primary">
            {isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: rawBody }} />
            ) : (
              <div className="space-y-4 whitespace-pre-line leading-relaxed text-gray-700 text-lg">
                {rawBody}
              </div>
            )}
          </article>

          {/* Contact Highlight Box inside article */}
          <div className="mt-14 p-8 rounded-3xl bg-gradient-to-br from-sky-50 via-white to-sky-50 border border-sky-100 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#1b3157] uppercase tracking-wider">
                <Sparkles className="w-4 h-4 text-[#85d7f9]" />
                <span>{t({ da: "FTR Service ApS", en: "FTR Service ApS" })}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {t({ da: "Ønsker du et gratis og uforpligtende tilbud?", en: "Want a free and non-binding offer?" })}
              </h3>
              <p className="text-sm text-gray-600">
                {t({
                  da: "Vi står altid klar til at hjælpe med privat-, erhvervs- og flytterengøring i Billund, Varde og Vejle.",
                  en: "We are always ready to help with private, business, and move-out cleaning in Billund, Varde and Vejle."
                })}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Link
                to="/kontakt"
                className="px-5 py-3 rounded-full bg-brand-primary text-white font-semibold text-sm hover:bg-[#152744] transition-all text-center shadow-md whitespace-nowrap"
              >
                {t({ da: "Kontakt os i dag", en: "Contact us today" })}
              </Link>
              <a
                href={`tel:${companyInfo?.telefon?.replace(/\s+/g, '') || '+4591847666'}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-gray-200 text-gray-800 font-semibold text-sm hover:bg-gray-50 transition-all text-center whitespace-nowrap"
              >
                <Phone className="w-4 h-4 text-[#1877F2]" />
                <span>{companyInfo?.telefon || '+45 91 84 76 66'}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Articles Section */}
      {recentArticles.length > 0 && (
        <section className="py-16 bg-gray-50 border-t border-gray-200/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {t({ da: "Flere nyheder og råd", en: "More news and advice" })}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t({ da: "Læs flere spændende artikler fra os", en: "Read more interesting articles from us" })}
                </p>
              </div>

              <Link
                to="/nyheder"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:text-[#1877F2] transition-colors"
              >
                <span>{t({ da: "Se alle nyheder", en: "View all news" })}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recentArticles.map((otherArticle, i) => {
                const otherTitle = getLocalizedField(otherArticle.titel);
                const otherExcerpt = getLocalizedField(otherArticle.uddrag);
                const otherSlug = getSlug(otherArticle);
                const otherImage = getImageSrc(otherArticle);
                const otherDate = formatDate(otherArticle.dato, language);

                return (
                  <Link
                    key={otherArticle.id || i}
                    to={`/nyheder/${otherSlug}`}
                    className="group bg-white rounded-3xl overflow-hidden shadow-xs hover:shadow-lg transition-all duration-300 border border-gray-100 flex flex-col"
                  >
                    <div className="h-44 overflow-hidden bg-gray-100 relative">
                      <img
                        src={otherImage}
                        alt={otherTitle}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      {otherDate && (
                        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-700">
                          {otherDate}
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div className="space-y-2">
                        <h3 className="font-bold text-gray-900 group-hover:text-[#1877F2] transition-colors line-clamp-2">
                          {otherTitle}
                        </h3>
                        <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">
                          {otherExcerpt}
                        </p>
                      </div>
                      <div className="pt-4 mt-3 border-t border-gray-50 flex items-center text-xs font-semibold text-brand-primary group-hover:text-[#1877F2]">
                        <span>{t({ da: "Læs mere", en: "Read more" })}</span>
                        <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
